import Dialog from '@mui/material/Dialog';
import { Bar } from 'react-chartjs-2';
import Directions from '../Directions';
import { useState, useContext, useEffect, useRef } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { SessionContext } from '../../Contexts/SessionContext';
import { APIContext } from '../../Contexts/APIContext';
import ExportTripRatePeakHourGraphDialog from '../../TripRate/Calculation/ExportTripRatePeakHourGraphDialog';
import ExportTripRatePeakHourGraphViewModel from '../../TripRate/ViewModels/ExportTripRatePeakHourGraphViewModel';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface IProps {
    onClose: () => void;
    open: boolean;
    vehicleType: number;
    surveyRefs: string[];
}

export default function PeakHourChart({ onClose, open, vehicleType, surveyRefs }: IProps) {
    const [direction, setDirection] = useState<string>('Total');
    const { sessionState } = useContext(SessionContext);
    const { post, postPDF } = useContext(APIContext);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [chartData, setChartData] = useState<PeakHourChartViewModel[]>([]);
    const chartRef = useRef<any>(null);

    useEffect(() => {
        post("Chart", `GetPeakHourSurveyCountsChart?headerIndex=${sessionState.primaryFilters.headerIndex}&direction=${direction}`,
            surveyRefs,
            setChartData
        );
    }, [direction]);

    const data = {
        labels: chartData.map(item => item.timeSlot),
        datasets: [
            {
                label: 'Peak Hour Frequency',
                data: chartData.map(item => item.count),
                backgroundColor: 'rgba(53, 162, 235, 0.5)',
                borderColor: 'rgba(53, 162, 235, 1)',
                borderWidth: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: `Peak Hour Distribution - ${direction}`,
                font: {
                    size: 14,
                    weight: 'bold' as const,
                },
                padding: 10,
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Time Period',
                    font: {
                        size: 12,
                        weight: 'bold' as const,
                    },
                },
                grid: {
                    display: true,
                    color: '#e0e0e0',
                },
                ticks: {
                    font: {
                        size: 10,
                    },
                    maxRotation: 45,
                    minRotation: 45,
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Number of Surveys',
                    font: {
                        size: 12,
                        weight: 'bold' as const,
                    },
                },
                beginAtZero: true,
                grid: {
                    display: true,
                    color: '#e0e0e0',
                },
                ticks: {
                    font: {
                        size: 10,
                    },
                    stepSize: 1,
                },
            },
        },
        layout: {
            padding: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 10,
            },
        },
    };

    const openDialog = () => {
        setDialogOpen(true)
    }

    const closeDialog = () => {
        setDialogOpen(false)
    }

    const exportGraph = (vm: ExportTripRatePeakHourGraphViewModel) => {
        if (vm.exportType == "JPEG") {
            if (chartRef.current) {
                // Get chart instance
                const chart = chartRef.current;
                const image = chart.toBase64Image('image/jpeg', 1.0);

                // Create download link
                const link = document.createElement('a');
                link.href = image;
                link.download = vm.fileName ?? 'PeakHourChart.jpg';
                link.click();
            }
            closeDialog();
            return;
        }
        vm.direction = direction;
        vm.mainUse = sessionState.tripRateMain;
        vm.subUse = sessionState.tripRateSub;
        vm.surveyType = sessionState.tripRateType
        vm.vehicleType = vehicleType;
        vm.peakHour = chartData;

        postPDF(`PDFGenerator`, `GetTripRatePeakHourGraphPDF`, vm.fileName ?? `Trip Rate Peak Hour Graph`, vm.displayToScreen, vm)
    }


    return (
        <Dialog open={open} fullWidth maxWidth="xl" onClose={onClose}>
            <Grid container>
                <Grid size={12}>
                    <div style={{ padding: '20px', height: '600px' }}>
                        <Directions setType={setDirection} type={direction} />
                        <Grid>
                            <Button fullWidth={false} onClick={openDialog}>Export</Button>
                        </Grid>
                        <div style={{ position: 'relative', height: '500px' }}>
                            <Bar ref={chartRef} data={data} options={options} />
                        </div>
                    </div>
                </Grid>

            </Grid>
            {dialogOpen && <ExportTripRatePeakHourGraphDialog dialogOpen={dialogOpen} handleClose={closeDialog} exportTripRate={exportGraph} />}

        </Dialog>
    );
}
